import { ApiError, getToken } from '@/lib/api';

const BASE = import.meta.env.VITE_SUPABASE_URL + '/functions/v1';
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface UploadedAttachment {
  id: string;
  filename?: string;
  url?: string;
}

/**
 * Uploads a single file (image or PDF) to the /attachments Cloud Function and
 * returns the new attachment id. Used by the Topic Import flow to upload a
 * flyer before invoking /extract-topic.
 *
 * Multipart contract matches the iOS AttachmentsAPI pattern: `file` field +
 * optional metadata. The endpoint returns `{ id, filename, url }`.
 */
export async function uploadAttachment(file: File): Promise<UploadedAttachment> {
  const headers = new Headers();
  headers.set('apikey', ANON);
  const tok = getToken();
  if (tok) headers.set('Authorization', `Bearer ${tok}`);
  // NOTE: do NOT set Content-Type — the browser must add the multipart boundary.

  const fd = new FormData();
  fd.append('file', file, file.name);
  fd.append('visibility', 'public');
  fd.append('language_code', 'all');
  fd.append('purpose', 'flyer');

  const res = await fetch(`${BASE}/attachments`, {
    method: 'POST',
    headers,
    body: fd,
  });
  const text = await res.text();
  let body: unknown = text;
  try {
    body = JSON.parse(text);
  } catch {
    /* leave as text */
  }
  if (!res.ok) {
    const msg = (body as { error?: string }).error ?? `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, body);
  }
  // Accept either { id, ... } or { attachment: { id, ... } }
  const obj = body as { id?: string; attachment?: UploadedAttachment };
  if (obj.attachment) return obj.attachment;
  if (obj.id) return obj as UploadedAttachment;
  throw new ApiError('attachments_response_missing_id', res.status, body);
}
